import { computed, ref, watch } from 'vue';
import { refDebounced } from '@vueuse/core';
import { useFormErrors } from '@/composables/useFormErrors';
import { useStudentDataRequestAudienceCount } from '@/composables/queries/useStudentDataRequestAudienceCount';
import { useStudentDataRequestMutations } from '@/composables/mutations/useStudentDataRequestMutations';
import {
  WIZARD_STEPS, audienceInput, createRequestInput, draftErrors, emptyDataRequestDraft, hasAudience, stepOfError,
  type DataRequest, type DataRequestDraft, type WizardStep,
} from '@/utils/studentDataRequests';

/**
 * The "new data request" wizard: the draft, the steps (each checked before
 * moving on), a live count of the students the audience reaches, and the
 * send, with server errors on their field and the wizard taken back to the
 * step that field is on.
 */
export function useStudentDataRequestWizard() {
  const draft = ref<DataRequestDraft>(emptyDataRequestDraft());
  const step = ref<WizardStep>('what');
  const { create } = useStudentDataRequestMutations();
  const { serverErrors, handleServerErrors, clearErrors, getFirstErrorMessage } = useFormErrors();

  const clientErrors = ref<Record<string, string>>({});
  /** Messages by key ("input.title", "input.fields", "input.audience", "input.due_on", …). */
  const errors = computed<Record<string, string>>(() => ({
    ...Object.fromEntries(Object.entries(serverErrors.value).map(([key, messages]) => [key, messages[0] ?? ''])),
    ...clientErrors.value,
  }));
  const generalError = ref('');

  const audience = computed(() => (hasAudience(draft.value) ? audienceInput(draft.value) : null));
  // A refused audience is answered by changing it: drop the stale error so it
  // never sits beside a fresh count.
  watch(audience, () => {
    if (serverErrors.value['input.audience']) {
      serverErrors.value = Object.fromEntries(Object.entries(serverErrors.value).filter(([key]) => key !== 'input.audience'));
    }
  }, { deep: true });
  const { count: audienceCount, loading: countingAudience } = useStudentDataRequestAudienceCount(refDebounced(audience, 300));

  const stepIndex = computed(() => WIZARD_STEPS.findIndex(s => s.value === step.value));
  const isLastStep = computed(() => stepIndex.value === WIZARD_STEPS.length - 1);

  function check(target: WizardStep): boolean {
    clientErrors.value = draftErrors(draft.value, target);
    return Object.keys(clientErrors.value).length === 0;
  }

  /** Move on if this step is complete. */
  function next(): boolean {
    clearErrors();
    generalError.value = '';
    if (!check(step.value)) return false;
    const following = WIZARD_STEPS[stepIndex.value + 1];
    if (following) step.value = following.value;
    return true;
  }

  function back() {
    clientErrors.value = {};
    const previous = WIZARD_STEPS[stepIndex.value - 1];
    if (previous) step.value = previous.value;
  }

  /** Send the request. Returns it, or null when something needs fixing first. */
  async function send(): Promise<DataRequest | null> {
    clearErrors();
    generalError.value = '';
    for (const { value } of WIZARD_STEPS) {
      if (!check(value)) {
        step.value = value;
        return null;
      }
    }

    try {
      const result = await create.mutate({ input: createRequestInput(draft.value) });
      return result?.data?.createStudentDataRequest ?? null;
    } catch (err) {
      handleServerErrors(err);
      const keys = Object.keys(serverErrors.value);
      if (keys.length) {
        step.value = stepOfError(keys[0] as string);
      } else {
        console.error(err);
        generalError.value = getFirstErrorMessage() || (err as Error)?.message || 'The request could not be sent. Please try again.';
      }
      return null;
    }
  }

  function reset() {
    draft.value = emptyDataRequestDraft();
    step.value = 'what';
    clientErrors.value = {};
    generalError.value = '';
    clearErrors();
  }

  return {
    draft, step, stepIndex, isLastStep, errors, generalError,
    audienceCount, countingAudience, sending: create.loading,
    next, back, send, reset,
  };
}
